import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typo, SimpleButton, Textinput, Flex, Icon } from '../../../../ui';
import { media } from '../../../../utils/media';
import { setTocStructure, selectGuideTocStructure } from '../../courseGuideState';
import { SUGGESTED_STRUCTURES, applySuggestedStructure } from '../../data/suggestedStructures';

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepHeader = styled.div`
  margin-bottom: 8px;
`;

const StructureCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;

  ${media.lessThan('tablet')} {
    grid-template-columns: 1fr;
  }
`;

const StructureCard = styled.button`
  padding: 16px;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  background: ${({ $active, theme }) => ($active ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_0)};
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY};
  }
`;

const TocTree = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.NEU_50};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const PageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 6px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.NEU_100};

  &:last-child {
    border-bottom: none;
  }
`;

const InlineInput = styled.input`
  border: none;
  background: transparent;
  font-size: 14px;
  font-family: inherit;
  padding: 4px 8px;
  flex: 1;
  border-radius: 4px;

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.NEU_0};
    border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  }
`;

const SectionInput = styled(InlineInput)`
  font-weight: 600;
  font-size: 15px;
`;

const BuildYourOwnHeader = styled(Flex)`
  margin-bottom: 12px;
`;

const AddPageButton = styled(SimpleButton)`
  margin-top: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border-radius: 8px;
  border: 2px dashed ${({ theme }) => theme.colors.NEU_200};
`;

const StepTocBuilder = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tocStructure = useSelector(selectGuideTocStructure);

  const handleApplyStructure = useCallback(
    (structure) => {
      const newToc = applySuggestedStructure(structure, t);
      dispatch(setTocStructure(newToc));
    },
    [dispatch, t],
  );

  const updateToc = useCallback(
    (newToc) => dispatch(setTocStructure(newToc)),
    [dispatch],
  );

  const handleSectionNameChange = useCallback(
    (sectionId, name) => {
      const newToc = tocStructure.map((s) => (s.id === sectionId ? { ...s, name } : s));
      updateToc(newToc);
    },
    [tocStructure, updateToc],
  );

  const handlePageNameChange = useCallback(
    (sectionId, pageId, name) => {
      const newToc = tocStructure.map((s) =>
        s.id === sectionId
          ? { ...s, pages: s.pages.map((p) => (p.id === pageId ? { ...p, name } : p)) }
          : s,
      );
      updateToc(newToc);
    },
    [tocStructure, updateToc],
  );

  const addSection = useCallback(() => {
    const id = `temp-section-${Date.now()}`;
    const pageId = `temp-page-${Date.now() + 1}`;
    updateToc([
      ...tocStructure,
      {
        id,
        name: t('courseGuide.tocBuilder.sectionName'),
        pages: [{ id: pageId, name: t('courseGuide.tocBuilder.pageName') }],
      },
    ]);
  }, [tocStructure, updateToc, t]);

  const addPage = useCallback(
    (sectionId) => {
      const pageId = `temp-page-${Date.now()}`;
      const newToc = tocStructure.map((s) =>
        s.id === sectionId
          ? { ...s, pages: [...s.pages, { id: pageId, name: t('courseGuide.tocBuilder.pageName') }] }
          : s,
      );
      updateToc(newToc);
    },
    [tocStructure, updateToc, t],
  );

  const removeSection = useCallback(
    (sectionId) => {
      updateToc(tocStructure.filter((s) => s.id !== sectionId));
    },
    [tocStructure, updateToc],
  );

  const removePage = useCallback(
    (sectionId, pageId) => {
      const newToc = tocStructure.map((s) =>
        s.id === sectionId ? { ...s, pages: s.pages.filter((p) => p.id !== pageId) } : s,
      );
      updateToc(newToc);
    },
    [tocStructure, updateToc],
  );

  return (
    <StepWrapper>
      <StepHeader>
        <Typo.ContentBigHeader>{t('courseGuide.tocBuilder.title')}</Typo.ContentBigHeader>
        <Typo.SmallText>{t('courseGuide.tocBuilder.subtitle')}</Typo.SmallText>
      </StepHeader>

      <div>
        <Typo.TextTitle $marginBottom='12px'>{t('courseGuide.tocBuilder.suggestedStructures')}</Typo.TextTitle>
        <StructureCardsGrid>
          {SUGGESTED_STRUCTURES.map((structure) => (
            <StructureCard
              key={structure.id}
              onClick={() => handleApplyStructure(structure)}
              type='button'
            >
              <Typo.TextTitle>{t(structure.nameKey)}</Typo.TextTitle>
              <Typo.SmallText>{t(structure.descriptionKey)}</Typo.SmallText>
            </StructureCard>
          ))}
        </StructureCardsGrid>
      </div>

      <div>
        <BuildYourOwnHeader justify='space-between' align='center'>
          <Typo.TextTitle>{t('courseGuide.tocBuilder.orBuildYourOwn')}</Typo.TextTitle>
          <SimpleButton variant='secondary' onClick={addSection} startIcon='Add' size='small'>
            {t('courseGuide.tocBuilder.addSection')}
          </SimpleButton>
        </BuildYourOwnHeader>

        {tocStructure.length === 0 ? (
          <EmptyState>
            <Typo.Text>{t('courseGuide.tocBuilder.emptyStructure')}</Typo.Text>
          </EmptyState>
        ) : (
          <TocTree>
            {tocStructure.map((section) => (
              <SectionBlock key={section.id}>
                <SectionHeader>
                  <Icon.Folder size='small' />
                  <SectionInput
                    value={section.name}
                    onChange={(e) => handleSectionNameChange(section.id, e.target.value)}
                    aria-label={t('courseGuide.tocBuilder.sectionName')}
                  />
                  <SimpleButton
                    variant='ghost'
                    size='small'
                    icon='Delete'
                    onClick={() => removeSection(section.id)}
                    noTooltip
                  />
                </SectionHeader>

                {section.pages.map((page) => (
                  <PageItem key={page.id}>
                    <Icon.Notes size='small' />
                    <InlineInput
                      value={page.name}
                      onChange={(e) => handlePageNameChange(section.id, page.id, e.target.value)}
                      aria-label={t('courseGuide.tocBuilder.pageName')}
                    />
                    <SimpleButton
                      variant='ghost'
                      size='small'
                      icon='Close'
                      onClick={() => removePage(section.id, page.id)}
                      noTooltip
                    />
                  </PageItem>
                ))}

                <AddPageButton
                  variant='ghost'
                  size='small'
                  startIcon='Add'
                  onClick={() => addPage(section.id)}
                >
                  {t('courseGuide.tocBuilder.addPage')}
                </AddPageButton>
              </SectionBlock>
            ))}
          </TocTree>
        )}
      </div>
    </StepWrapper>
  );
};

export default StepTocBuilder;
